import { colors as themeColors } from '../../../utils/theme';
import { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Button, useTheme, useMediaQuery, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

// Utility: Normalize object keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return {};
    return Object.fromEntries(Object.entries(obj).map(([key, val]) => [key.toLowerCase(), val]));
};

const Section = ({ title, data, stepIndex, onEditStep, onChangeQuotation }) => {
    // If no data or empty object, only render header
    if (data == null || (typeof data === 'object' && Object.keys(data).length === 0)) {
        return (
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" >{title}</Typography>
                    <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}> Edit </Button>
                </Box>
            </Paper>
        );
    }

    // Normalize client.details sub-object
    const contact = normalizeKeys(data.details);

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{title}</Typography>
                <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}> Edit </Button>
            </Box>

            {title === 'Client Details' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    {data.companyName && (
                        <Grid item xs={12} sm={6} md={5}>
                            <Typography component="span" variant="body2" color="text.secondary"> 🏨 Company:&nbsp; </Typography>
                            <Typography component="span" variant="body2" fontWeight="500"> {data.companyName} </Typography>
                        </Grid>
                    )}
                    <Grid container spacing={1} mt={0.5} alignItems="center">
                        {data.qoutationNumber && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box>
                                        <Typography component="span" variant="body2" color="text.secondary"> Quotation No.:&nbsp; </Typography>
                                        <Typography component="span" variant="body2" fontWeight="500"> {data.qoutationNumber} </Typography>
                                    </Box>
                                    <Button size="small" variant="outlined" color="primary" onClick={onChangeQuotation} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}> Change </Button>
                                </Box>
                            </Grid>
                        )}
                        {data.inquiryDate && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary"> 📅 Date:&nbsp; </Typography>
                                <Typography component="span" variant="body2" fontWeight="500"> {data.inquiryDate} </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Grid container spacing={1} mt={0.5}>
                        {contact.contactperson && (
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography component="span" variant="body2" color="text.secondary"> 👤 Contact:&nbsp; </Typography>
                                <Typography component="span" variant="body2" fontWeight="500"> {contact.contactperson} </Typography>
                            </Grid>
                        )}
                        {contact.mobileno && (
                            <Grid item xs={12} sm={6} md={2}>
                                <Typography component="span" variant="body2" color="text.secondary"> 📞 {contact.mobileno} </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container spacing={1} mt={0.5}>
                        {contact.email && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary"> ✉️ {contact.email} </Typography>
                            </Grid>
                        )}
                        {(contact.city || contact.state) && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center">
                                    {contact.city && (<Typography variant="body2" color="text.secondary"> 🏙️ {contact.city} </Typography>)}
                                    {contact.city && contact.state && (<Typography sx={{ mx: 0.5 }} variant="body2" color="text.secondary"> , </Typography>)}
                                    {contact.state && (<Typography variant="body2" color="text.secondary"> {contact.state} </Typography>)}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

        </Paper>
    );
};

const InquiryReview = ({ formData, onSubmit, onBack, onEditStep, mode, selectedCompany, isQuotationLocked, connectionString }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    console.log(formData);
    const { client, product, consigner, transporter, export: exportData } = formData;
    const isSameState = (formData?.client?.details?.State || '').toUpperCase() === (selectedCompany?.State || '').toUpperCase();
    console.log(isSameState);

    const [openChangeDialog, setOpenChangeDialog] = useState(false);
    const [newSeriesNumber, setNewSeriesNumber] = useState('');
    const [changeDialogError, setChangeDialogError] = useState('');
    const [changeDialogLoading, setChangeDialogLoading] = useState(false);

    // ──────────────── Alt+S (Submit/Update) shortcut ───────────────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    // This is the wizard's final step — Alt+S here is what actually calls the
    // add/update API (InquiryMaster.jsx's handleSubmit), not just step navigation.
    // Alt+C (Cancel) is handled once in the parent (InquiryMaster.jsx) so it
    // works from every step, not duplicated here.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyS") return;
            e.preventDefault();
            if (!isQuotationLocked) {
                onSubmit();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    const parseQuotationNumber = (quotationNo) => {
        if (!quotationNo) return { prefix: '', series: '', suffix: '' };
        const parts = quotationNo.split('/');
        if (parts.length !== 3) return { prefix: '', series: '', suffix: '' };
        return {
            prefix: parts[0],
            series: parts[1],
            suffix: parts[2]
        };
    };

    const buildQuotationNumber = (prefix, series, suffix) => {
        return `${prefix}/${series}/${suffix}`;
    };

    const handleChangeQuotation = () => {
        const currentQNo = client?.qoutationNumber;
        if (!currentQNo) return;
        const parsed = parseQuotationNumber(currentQNo);
        setNewSeriesNumber(parsed.series);
        setChangeDialogError('');
        setOpenChangeDialog(true);
    };

    // ✅ Validate series number: exactly 4 digits, no special characters
    const validateSeriesNumber = (series) => {
        const trimmed = series.trim();

        // Check if it contains only digits
        if (!/^\d+$/.test(trimmed)) {
            return { valid: false, error: 'Series number must contain only digits (0-9)' };
        }

        // Check if it's more than 4 digits
        if (trimmed.length > 4) {
            return { valid: false, error: 'Series number must be exactly 4 digits maximum' };
        }

        // Pad to 4 digits
        const normalized = trimmed.padStart(4, '0');
        return { valid: true, normalized };
    };

    const handleConfirmChange = async () => {
        if (!newSeriesNumber.trim()) {
            setChangeDialogError('Series number cannot be empty');
            return;
        }

        // ✅ Validate series number format
        const validation = validateSeriesNumber(newSeriesNumber);
        if (!validation.valid) {
            setChangeDialogError(validation.error);
            return;
        }

        const currentQNo = client?.qoutationNumber;
        const parsed = parseQuotationNumber(currentQNo);
        const newQuotationNumber = buildQuotationNumber(parsed.prefix, validation.normalized, parsed.suffix);

        if (newQuotationNumber === currentQNo) {
            setChangeDialogError('New quotation number must be different from current');
            return;
        }

        setChangeDialogLoading(true);
        setChangeDialogError('');

        try {
            const companyId = selectedCompany?.CompId || selectedCompany?.companyId || selectedCompany?.id;

            if (!companyId) {
                console.error('Company ID not found. selectedCompany:', selectedCompany);
                setChangeDialogError('Company information not available. Please ensure company is selected.');
                setChangeDialogLoading(false);
                return;
            }

            console.log('Checking duplicate number:', { module: 'Quotation', numberValue: newQuotationNumber, companyId });

            const response = await axios.post(`${connectionString}/api/check-duplicate-number`, {
                module: 'Quotation',
                numberValue: newQuotationNumber,
                companyId: companyId
            });

            if (response.data?.exists) {
                setChangeDialogError('This quotation number already exists');
            } else {
                client.qoutationNumber = newQuotationNumber;
                setOpenChangeDialog(false);
                setNewSeriesNumber('');
            }
        } catch (error) {
            console.error('Error checking quotation duplicate:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Error validating quotation number';
            setChangeDialogError(errorMsg);
        } finally {
            setChangeDialogLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenChangeDialog(false);
        setNewSeriesNumber('');
        setChangeDialogError('');
    };

    const formatINR = (value) => {
        if (isNaN(value)) return "₹0.00";
        return value.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, });
    };

    const renderProductCards = () => {
        if (!product?.tableData?.length) {
            return (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                    No product data available.
                </Typography>
            );
        }

        return (
            <Box>
                {[...product.tableData].filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).reverse().map((row, index) => {
                    const { productCode, description, unit, tax, qty, rate, disc, } = row;
                    const amount = (qty * rate).toFixed(2);
                    const discAmount = (qty * rate * (1 - disc / 100)).toFixed(2);
                    const gstAmount = ((discAmount * tax) / 100).toFixed(2);

                    return (
                        <Box key={index} sx={{ border: `1px solid ${themeColors.neutral.lineStrong}`, borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: themeColors.neutral.surface, }} >
                            {/* Line 1 */}
                            <Typography sx={{ fontWeight: 600 }}>
                                {productCode} —{" "}
                                <Box component="span" sx={{ fontStyle: "italic", fontWeight: "normal", display: "inline", wordBreak: "break-word", whiteSpace: "normal" }} >
                                    {description}
                                </Box>
                            </Typography>

                            {/* Qty */}
                            <Grid container justifyContent="space-between">
                                <Typography>Qty:</Typography>
                                <Typography>{qty}</Typography>
                            </Grid>

                            {/* Rate */}
                            <Grid container justifyContent="space-between">
                                <Typography>Rate:</Typography>
                                <Typography>₹{rate}</Typography>
                            </Grid>

                            {/* Discount */}
                            <Grid container justifyContent="space-between">
                                <Typography>Disc:</Typography>
                                <Typography>{disc}%</Typography>
                            </Grid>

                            {/* TAX INFO */}
                            <Grid container justifyContent="space-between">
                                <Typography>GST:</Typography>
                                <Typography>{row.tax}%</Typography>
                            </Grid>

                            {/* GST depending on state */}
                            <Grid container justifyContent="space-between">
                                <Typography>{isSameState ? "CGST+SGST:" : "IGST:"}</Typography>
                                <Typography>
                                    ₹
                                    {isSameState ? `${(gstAmount / 2).toFixed(2)} + ${(gstAmount / 2).toFixed( 2 )}` : gstAmount}
                                </Typography>
                            </Grid>

                            {/* Final Amount */}
                            <Grid container justifyContent="space-between">
                                <Typography>Amount:</Typography>
                                <Typography>₹{discAmount}</Typography>
                            </Grid>

                            {/* Optional Fields */}
                            {row.unit && (
                                <Grid container justifyContent="space-between">
                                    <Typography>Unit:</Typography>
                                    <Typography>{unit}</Typography>
                                </Grid>
                            )}

                            {row.remark && row.remark.trim() !== '' && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal' }} >
                                    Remark: {row.remark}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'grey.50', minHeight: '100vh' }}>
            <Typography variant="h5" gutterBottom color="primary.main"> 📋 Review Quotation Summary </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}> Please review all details before submission. Use the back button to make changes if needed. </Typography>

            <Section title="Client Details" data={client} stepIndex={0} onEditStep={onEditStep} onChangeQuotation={handleChangeQuotation} />

            <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: themeColors.neutral.surface }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary.main">Product Details</Typography>
                    {product?.tableData?.length > 0 && (
                        <Button size="small" variant="outlined" color="primary" onClick={() => onEditStep(1)}> Edit </Button>
                    )}
                </Box>
                <Divider sx={{ mb: 2, bgcolor: 'grey.200' }} />
                {isMobile ? (
                    renderProductCards()
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <DataGrid getRowHeight={() => 'auto'} autoHeight hideFooter disableRowSelectionOnClick rows={[...(product?.tableData || [])].filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300)
                            .reverse()
                            .map((r, i) => ({ ...r, id: i + 1, index: i })) || []}
                            columns={[
                                { field: 'id', headerName: '#', width: 60, align: 'center', headerAlign: 'center' },
                                {
                                    field: 'product', headerName: 'Product', flex: 1, minWidth: 180, sortable: false, filterable: false,
                                    renderCell: (params) => {
                                        const { productCode, description, unit, tax, size, color, batchNo, expDate, serialNo, remark, qty, rate } = params.row;
                                        const gstAmount = ((qty * rate * tax) / 100).toFixed(2);
                                        return (
                                            <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body2" fontWeight="bold" component="div">
                                                    <Box component="span" sx={{ fontSize: '0.85rem' }}>{productCode}</Box>
                                                </Typography>
                                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>{description}</Typography>
                                                <Box display="flex" flexDirection="column">
                                                    <Typography variant="caption">Unit: {unit} | GST: {tax}%</Typography>
                                                    <Typography variant="caption" color="text.secondary"> {isSameState ? `SGST: ₹${(gstAmount / 2).toFixed(2)} + CGST: ₹${(gstAmount / 2).toFixed(2)}` : `IGST: ₹${gstAmount}`} </Typography>
                                                    {(size || color) && (<Typography variant="caption"> {size && `Size: ${size}`} {color && `| Color: ${color}`} </Typography>)}
                                                    {(batchNo || expDate || serialNo) && (<Typography variant="caption"> {batchNo && `Batch: ${batchNo}`} {expDate && `| Exp: ${expDate}`} {serialNo && `| S/N: ${serialNo}`} </Typography>)}
                                                    {remark && (<Typography variant="caption" color="text.secondary"> Remark: {remark} </Typography>)}
                                                </Box>
                                            </Box>
                                        );
                                    },
                                },
                                { field: 'qty', headerName: 'Qty', width: 70, align: 'center', headerAlign: 'center' },
                                { field: 'rate', headerName: 'Rate', width: 110, align: 'right', headerAlign: 'center', renderCell: (params) => formatINR(Number(params.value) || 0) },
                                {
                                    field: 'disc', headerName: 'Disc (%)', width: 70, align: 'center', headerAlign: 'center',
                                    renderHeader: () => (
                                        <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                                            <Typography variant="body2">Disc</Typography>
                                            <Typography variant="caption" color="text.secondary">(%)</Typography>
                                        </Box>
                                    ),
                                },
                                { field: 'amount', headerName: 'Amount ₹', width: 110, align: 'right', headerAlign: 'right',
                                    renderCell: (params) => {
                                        const qty = Number(params.row.qty) || 0;
                                        const rate = Number(params.row.rate) || 0;
                                        const disc = Number(params.row.disc) || 0;
                                        const total = qty * rate * (1 - disc / 100);
                                        return formatINR(total);
                                    },
                                },

                            ]}
                            sx={{ mt: 5, border: `1px solid ${themeColors.neutral.lineStrong}`, borderRadius: 1,
                                '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface }
                            }}
                        />
                    </Box>
                )}

                {product?.tableData?.length > 0 && (
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Box
                            sx={{ border: `1px solid ${themeColors.neutral.line}`, borderRadius: 2, p: 1.5, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 280 }, bgcolor: themeColors.neutral.surfaceAlt, }} >
                            {(() => {
                                const s = product?.summary || {};
                                const items = [{ label: 'Basic Amount:', value: `₹${s.basic?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },];

                                if (s.igst > 0) {
                                    items.push({ label: 'IGST:', value: `₹${s.igst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, });
                                } else {
                                    if (s.cgst > 0) {
                                        items.push({ label: 'CGST:', value: `₹${s.cgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, });
                                    }
                                    if (s.sgst > 0) {
                                        items.push({ label: 'SGST:', value: `₹${s.sgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, });
                                    }
                                }

                                items.push({ label: 'Round Off:', value: `₹${s.roundOff?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }, { label: 'Grand Total:', value: `₹${s.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });

                                return items.map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, }} >
                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ));
                            })()}
                        </Box>
                    </Box>
                )}
            </Paper>

            <Box mt={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Button variant="outlined" color="primary" onClick={onBack} sx={{ minWidth: 100 }}> Back </Button>
                <Button variant="contained" color="primary" onClick={onSubmit} sx={{ minWidth: 100 }} disabled={isQuotationLocked}> {mode === "edit" ? "UPDATE" : "SUBMIT"} </Button>
            </Box>

            <Dialog open={openChangeDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Change Quotation Number</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {changeDialogError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {changeDialogError}
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Current Quotation No: <strong>{client?.qoutationNumber}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Format: Prefix / Series Number / Suffix (you can only change the series number)
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Series Number"
                        value={newSeriesNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            // ✅ Only allow digits and max 4 characters
                            const filtered = value.replace(/\D/g, '').slice(0, 4);
                            setNewSeriesNumber(filtered);
                        }}
                        disabled={changeDialogLoading}
                        placeholder="e.g., 00002"
                        variant="outlined"
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={changeDialogLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmChange}
                        variant="contained"
                        disabled={changeDialogLoading}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        {changeDialogLoading && <CircularProgress size={20} />}
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InquiryReview;